import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import {
  applyColorScheme,
  COLOR_SCHEME_APPLIED_EVENT,
  getEffectiveColorScheme,
  getStoredColorScheme,
  getSystemColorScheme,
  type ColorScheme,
} from '../lib/color-scheme';

@customElement('color-scheme-toggle')
export class ColorSchemeToggle extends LitElement {
  @state() private scheme: ColorScheme = 'dark';

  static styles = css`
    :host {
      display: block;
    }

    button {
      -webkit-app-region: no-drag;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: var(--toggle-size, 2.5rem);
      height: var(--toggle-size, 2.5rem);
      padding: 0;
      border: 1px solid rgba(255, 255, 255, 0.35);
      border-radius: var(--radius-full, 9999px);
      background: rgba(255, 255, 255, 0.12);
      color: #ffffff;
      cursor: pointer;
      transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
    }

    button:hover {
      background: rgba(255, 255, 255, 0.22);
      border-color: rgba(255, 255, 255, 0.55);
    }

    button:focus-visible {
      outline: 2px solid var(--focus-visible, #4a8bc8);
      outline-offset: 2px;
    }

    :host([data-surface='page']) button {
      border-color: var(--border-card, rgba(65, 65, 65, 0.5));
      background: var(--bg-soft, rgba(255, 255, 255, 0.06));
      color: var(--text);
    }

    :host([data-surface='page']) button:hover {
      background: var(--surface-raised, #141414);
      border-color: var(--brand-accent-soft, #fdc06a);
    }

    svg {
      width: var(--toggle-icon-size, 1.125rem);
      height: var(--toggle-icon-size, 1.125rem);
    }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    this.scheme = getEffectiveColorScheme();
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', this.onSystemSchemeChange);
    window.addEventListener(COLOR_SCHEME_APPLIED_EVENT, this.onSchemeApplied);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', this.onSystemSchemeChange);
    window.removeEventListener(COLOR_SCHEME_APPLIED_EVENT, this.onSchemeApplied);
  }

  private readonly onSchemeApplied = (e: Event): void => {
    const scheme = (e as CustomEvent<{ scheme: ColorScheme }>).detail?.scheme;
    if (scheme === 'light' || scheme === 'dark') {
      this.scheme = scheme;
    }
  };

  private readonly onSystemSchemeChange = (): void => {
    if (getStoredColorScheme() === null) {
      const next = getSystemColorScheme();
      applyColorScheme(next, false);
      this.scheme = next;
    }
  };

  private toggle(): void {
    const next: ColorScheme = this.scheme === 'light' ? 'dark' : 'light';
    applyColorScheme(next, true);
    this.scheme = next;
  }

  render() {
    const isDark = this.scheme === 'dark';
    const label = isDark ? 'Switch to light mode' : 'Switch to dark mode';
    return html`
      <button type="button" @click="${this.toggle}" aria-label="${label}" title="${label}">
        ${isDark
          ? html`<svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
              <path
                d="M12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12Zm0-16a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0V3a1 1 0 0 1 1-1Zm0 18a1 1 0 0 1-1-1v-1a1 1 0 1 1 2 0v1a1 1 0 0 1-1 1ZM5.64 5.64a1 1 0 0 1 1.41 0l.71.71a1 1 0 1 1-1.41 1.41l-.71-.71a1 1 0 0 1 0-1.41ZM16.24 16.24a1 1 0 0 1 1.41 0l.71.71a1 1 0 0 1-1.41 1.41l-.71-.71a1 1 0 0 1 0-1.41ZM3 13a1 1 0 0 1 1-1h1a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1Zm16 0a1 1 0 0 1 1-1h1a1 1 0 1 1 0 2h-1a1 1 0 0 1-1-1ZM7.05 18.36a1 1 0 0 1 0 1.41l-.71.71a1 1 0 0 1-1.41-1.41l.71-.71a1 1 0 0 1 1.41 0ZM17.66 7.76a1 1 0 0 1 0 1.41l-.71.71a1 1 0 1 1-1.41-1.41l.71-.71a1 1 0 0 1 1.41 0Z"
              />
            </svg>`
          : html`<svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
              <path
                d="M21.64 13a9 9 0 0 1-9.55 9.49A9 9 0 0 1 9.49 3.91 1 1 0 0 1 10 5.05 7 7 0 0 0 18.95 14a1 1 0 0 1 1.69.72v.01Z"
              />
            </svg>`}
      </button>
    `;
  }
}
